import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { HeaderFinishQuiz } from "./quiz-finish-header/quiz-finish-header";
import { QuizFinishBlock } from "./quiz-finish-block/quiz-finish-block";
import { assessmentFlowContainerStyle } from "../assessment-layout-styles";
import type { AssessmentRunningScoreBreakdown } from "../quiz-results-circular/quiz-results-circular";
import {
  containerStyle,
  assessmentFinishHeaderStyle,
  headerBoxStyle,
  contentBoxStyle,
  footerBoxStyle,
  continueButtonStyle,
} from "./styles";

interface QuizFinishProps {
  totalQuestions: number;
  currentQuestionIndex: number;
  onClose: () => void;
  onContinue?: () => void;
  // Time spent in seconds
  timeSpent: number;
  scoresEarned?: number;
  totalMaxScore?: number;
  bonusScore?: number;
  scoreBreakdown?: AssessmentRunningScoreBreakdown | null;
  hideTimeSpent?: boolean;
  continueLabel?: string;
  continueDisabled?: boolean;
  continueLoading?: boolean;
  resultsError?: string | null;
  onRetryResults?: () => void;
  assessmentData?: {
    scoresEarned: number;
    totalMaxScore: number;
    bonusScore?: number;
    focus: string;
  };
  fullScreenOnMobile?: boolean;
  isContest?: boolean;
  contestTitle?: string | null;
}

export const QuizFinish = ({
  totalQuestions,
  currentQuestionIndex,
  onClose,
  onContinue,
  timeSpent,
  scoresEarned,
  totalMaxScore,
  bonusScore,
  scoreBreakdown = null,
  hideTimeSpent = false,
  continueLabel = "View challenge results",
  continueDisabled = false,
  continueLoading = false,
  resultsError = null,
  onRetryResults,
  assessmentData,
  fullScreenOnMobile = false,
  isContest = false,
  contestTitle = null,
}: QuizFinishProps) => {
  const progress =
    assessmentData &&
    totalMaxScore &&
    totalMaxScore > 0 &&
    scoresEarned !== undefined
      ? Math.min(100, Math.round((scoresEarned / totalMaxScore) * 100))
      : Math.min(
          100,
          Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100),
        );

  return (
    <Box sx={fullScreenOnMobile ? assessmentFlowContainerStyle : containerStyle}>
      <Box sx={fullScreenOnMobile ? assessmentFinishHeaderStyle : headerBoxStyle}>
        <HeaderFinishQuiz
          progress={progress}
          isContest={isContest}
          contestTitle={contestTitle}
        />
      </Box>
      <Box sx={contentBoxStyle}>
        <QuizFinishBlock
          totalAnswers={totalQuestions}
          timeSpent={timeSpent}
          scoresEarned={scoresEarned}
          totalMaxScore={totalMaxScore}
          bonusScore={bonusScore}
          scoreBreakdown={scoreBreakdown}
          hideTimeSpent={hideTimeSpent}
          assessmentData={assessmentData}
        />
      </Box>
      <Box sx={footerBoxStyle}>
        {resultsError ? (
          <Alert
            severity="error"
            sx={{ mb: 1.5, width: "100%", maxWidth: 420 }}
            action={
              onRetryResults ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onRetryResults}
                  disabled={continueLoading}
                >
                  Retry
                </Button>
              ) : undefined
            }
          >
            {resultsError}
          </Alert>
        ) : null}
        <Button
          onClick={onContinue ?? onClose}
          sx={continueButtonStyle}
          disabled={continueDisabled || continueLoading}
          startIcon={
            continueLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {continueLoading ? "Loading results..." : continueLabel}
        </Button>
      </Box>
    </Box>
  );
};
